"use client";

import Link from "next/link";
import Image from "next/image";
import { motion } from "motion/react";
import {
  Calendar,
  Clock,
  User,
  ArrowRight,
  TrendingUp,
  BookOpen,
} from "lucide-react";
import { calculateReadingTime, cleanHeaderTags } from "@/_helper/helper";
import { useAppSelector } from "@/_hooks/hooks";

export default function BlogsClient({ blogList }: { blogList: any[] }) {
  const BannerList = useAppSelector((state) => state.home.BannerList);
  const banner: any =
    BannerList?.find((banner: any) => banner?.BannerType === "blogs") || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-orange-50/30 to-white">
      {/* Hero Section */}
      <section className="relative h-[50vh] overflow-hidden">
        {banner?.BannerImage && (
          <Image
            src={banner?.BannerImage || ""}
            alt={banner?.BannerTitle || "Banner Image"}
            fill
            className="object-cover"
            priority
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 via-gray-900/0 to-gray-900/0 pointer-events-none"></div>

        <div className="absolute inset-0 flex items-end pointer-events-none">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-7xl font-bold text-amber-300 mb-6 drop-shadow-2xl">
                {banner?.BannerTitle || "Travel Stories & Guides"}
              </h1>
              {banner?.Description && (
                <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto drop-shadow-lg mb-6">
                  {banner?.Description ||
                    "Travel tips, stories, and insights to enhance your SOU experience"}
                </p>
              )}
            </motion.div>
          </div>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Featured Blogs */}
        {blogList.length > 0 && (
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <TrendingUp className="w-6 h-6 text-orange-600" />
              <h2 className="text-3xl font-bold text-gray-900">
                Featured Stories
              </h2>
            </div>
            <div className="grid lg:grid-cols-2 gap-8">
              {blogList?.slice(0, 2).map((blog, index) => {
                const readingTime =
                  blog?.ReadingTime ||
                  calculateReadingTime(blog?.LongDescription || "");
                return (
                  <motion.div
                    key={blog?.ID}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Link
                      href={`/blogs/${blog?.BlogURL}`}
                      className="group block bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 hover:border-orange-200 h-full"
                    >
                      <div className="relative aspect-[16/9] overflow-hidden">
                        <Image
                          src={blog?.BlogBannerImage || blog?.Image}
                          alt={blog?.BlogTitle || blog?.Title}
                          fill
                          className="object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none"></div>
                        {/* <div
                          className={`absolute top-4 left-4 bg-gradient-to-r ${blog?.categoryColor} text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg`}
                        >
                          {blog?.category}
                        </div> */}
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-xl font-medium text-white mb-2 group-hover:text-amber-300 transition-colors">
                            {blog?.BlogTitle || blog?.Title}
                          </h3>
                        </div>
                      </div>
                      <div className="p-6">
                        <p
                          className="text-gray-600 mb-4 line-clamp-2 blog-content"
                          dangerouslySetInnerHTML={{
                            __html: cleanHeaderTags(
                              blog?.ShortDescription || blog?.Description,
                            ),
                          }}
                        />
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <div className="flex items-center gap-4">
                            {/* <div className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              <span>{blog?.author}</span>
                            </div> */}
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{blog?.BlogDate}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>{readingTime}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}

        {/* All Blog Posts */}
        {blogList && blogList?.length > 2 && (
          <section>
            <div className="flex items-center gap-3 mb-8">
              <BookOpen className="w-6 h-6 text-orange-600" />
              <h2 className="text-3xl font-bold text-gray-900">
                Latest Articles
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogList?.slice(2)?.map((blog: any, index: number) => {
                const readingTime =
                  blog?.ReadingTime ||
                  calculateReadingTime(
                    blog?.ShortDescription || blog?.Description || "",
                  );
                return (
                  <motion.div
                    key={blog?.ID}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Link
                      href={`/blogs/${blog?.BlogURL}`}
                      className="group block bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 hover:border-orange-200 h-full"
                    >
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <Image
                          src={blog?.BlogBannerImage || blog?.Image}
                          alt={blog?.BlogTitle || blog?.Title}
                          fill
                          className="object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none"></div>
                        {/* <div
                        className={`absolute top-4 left-4 bg-gradient-to-r ${blog?.categoryColor} text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg`}
                      >
                        {blog?.category}
                      </div> */}
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors line-clamp-2">
                          {blog?.BlogTitle}
                        </h3>
                        {/* <p
                          className="text-gray-600 mb-4 line-clamp-2 text-sm min-h-[2.5rem]"
                          dangerouslySetInnerHTML={{
                            __html: cleanHeaderTags(
                              blog?.ShortDescription || blog?.Description,
                            ),
                          }}
                        /> */}
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                          {/* <div className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          <span>{blog?.author}</span>
                        </div> */}
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{readingTime}</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          <span className="text-gray-500 text-xs">
                            {blog?.BlogDate}
                          </span>
                          <ArrowRight className="w-4 h-4 text-orange-600 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {/* Newsletter Section */}
      {/* <section className="bg-gradient-to-r from-orange-600 via-amber-600 to-orange-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <BookOpen className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Never Miss a Story
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Subscribe to our newsletter for the latest travel tips and stories
          </p>
          <div className="max-w-md mx-auto flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <button className="px-6 py-3 bg-white text-orange-600 font-bold rounded-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              Subscribe
            </button>
          </div>
        </div>
      </section> */}
    </div>
  );
}
